import * as THREE from "three";
import { Signal, type App } from "@engine";
import type { Player, PickupHitEvent } from "../player/Player";
import type { ScoreSystem } from "../scoring/ScoreSystem";
import type { HUD } from "../ui/HUD";
import type { Sparks } from "../fx/Sparks";
import type { SegmentFactory, PickupInstance } from "../level/SegmentFactory";
import type { LevelStreamer } from "../level/LevelStreamer";
import type { PickupType } from "../level/types";

const SCORE_BONUS = 250;
const SLOWMO_DURATION_S = 2.0;
const SLOWMO_SCALE = 0.55;

// Magnet: how long the pull-in effect lasts after consume. 5s lines
// up with the typical "two-segment runway" so the player gets a clean
// sweep across two safe panels before the field drops.
const MAGNET_DURATION_S = 5.0;
// Maximum z-distance from the player at which a score orb starts being
// pulled. Beyond this we don't bother iterating -- orbs further away
// will enter range as they scroll forward anyway.
const MAGNET_RANGE_Z = 6.0;
// How fast pickups lerp toward the player while inside the magnet
// field. exp(-LAMBDA * dt) per frame; 8 is "snappy but visible".
const MAGNET_LAMBDA = 8.0;

// Double: 2x reward window. 10s is long enough to clear ~2 full
// segments at mid speeds; short enough that stacking with Frenzy
// doesn't trivialise score-chasing achievements.
const DOUBLE_DURATION_S = 10.0;
const DOUBLE_MUL = 2.0;

const SHIELD_COLOR = 0x6bd2ff;
const SLOWMO_COLOR = 0xff5cb1;
const SCORE_COLOR = 0xffd23f;
const MAGNET_COLOR = 0xb265ff;
const DOUBLE_COLOR = 0xffe070;

// PickupManager owns the runtime side of pickups: routes Player.onPickup
// to score / shield / slow-mo behaviors, drives floating popups, sparks,
// and HUD chips. It also owns the slow-mo timer (counted on real time so
// it survives the ~0.32s gate-transition slow-mo windows: gate restores
// app.time.scale to 1.0 when its window closes, then PickupManager's
// timer continues in unscaledDt and re-asserts SLOWMO_SCALE next frame).
//
// reset() must be called on startGame, restartFromPause, quitToMenu and
// onDeath so the player never inherits leftover shield charge / dilation
// across runs.
export class PickupManager {
  // Emitted whenever a pickup is consumed (after the effect is
  // applied). StatsService subscribes to bump per-run counters; the
  // achievement layer reads them to fire magnetised / hard_runner /
  // ... unlocks.
  readonly onPickupConsumed = new Signal<{ type: PickupType }>();

  private app: App;
  private player: Player;
  private score: ScoreSystem;
  private hud: HUD;
  private sparks: Sparks;
  private factory: SegmentFactory;
  private streamer: LevelStreamer;
  private unsub: (() => void) | null = null;
  private shieldUnsub: (() => void) | null = null;

  private slowmoRemaining = 0;
  private magnetRemaining = 0;
  private doubleRemaining = 0;
  // Score multiplier coming from the active difficulty profile. The
  // double-pickup window multiplies on top of this. Both flow into
  // ScoreSystem.setRewardMultiplier whenever either side changes.
  private difficultyScoreMul = 1;
  // Optional whitelist of pickup types that may be consumed. null
  // means "all types allowed" (default). Daily "shield_off" sets
  // ["score","slowmo"] so shield orbs that physically spawn just
  // pass through without effect.
  private allowedTypes: Set<PickupType> | null = null;
  // Reusable scratch vectors so the per-frame magnet pull-in doesn't
  // allocate. `_local` holds the player position translated into the
  // pickup mesh's parent frame; `_world` is the player world position.
  private _world = new THREE.Vector3();
  private _local = new THREE.Vector3();

  constructor(
    app: App,
    player: Player,
    score: ScoreSystem,
    hud: HUD,
    sparks: Sparks,
    factory: SegmentFactory,
    streamer: LevelStreamer
  ) {
    this.app = app;
    this.player = player;
    this.score = score;
    this.hud = hud;
    this.sparks = sparks;
    this.factory = factory;
    this.streamer = streamer;

    this.unsub = this.player.onPickup.on((ev: PickupHitEvent) => {
      this.handlePickup(ev);
    });
    this.shieldUnsub = this.player.onShieldConsumed.on(() => {
      this.hud.setShield(false);
      this.hud.spawnPopup("BLOCKED", "warm");
      this.hud.flash("gate", "#6bd2ff");
      this.sparks.burstAt(this.player.mesh.position, SHIELD_COLOR, 24);
    });
  }

  // Tick on real (unscaled) time so the slow-mo timer behaves correctly
  // even while it is itself dilating the world. MorphHexGame.update wires
  // this in.
  update(unscaledDt: number): void {
    if (this.slowmoRemaining > 0) {
      this.slowmoRemaining = Math.max(0, this.slowmoRemaining - unscaledDt);
      // Re-assert each frame so the gate transition (which also writes
      // app.time.scale) doesn't leave us at 1.0 mid-window.
      if (this.slowmoRemaining > 0) {
        this.app.time.scale = SLOWMO_SCALE;
        this.hud.setSlowMoTimer(this.slowmoRemaining);
      } else {
        this.app.time.scale = 1.0;
        this.hud.setSlowMoTimer(null);
      }
    }

    // Magnet: pull every uncollected score orb in active segments
    // toward the player while the field is up. Iterates only when
    // active, so this is free in 99% of frames.
    if (this.magnetRemaining > 0) {
      this.magnetRemaining = Math.max(0, this.magnetRemaining - unscaledDt);
      if (this.magnetRemaining > 0) {
        this.applyMagnetField(unscaledDt);
        this.hud.setMagnetTimer(this.magnetRemaining);
      } else {
        this.hud.setMagnetTimer(null);
      }
    }

    // Double-score window: only manages its timer + HUD chip + reward
    // mul. The actual multiplier is composed in pushRewardMultiplier
    // and fed into the ScoreSystem.
    if (this.doubleRemaining > 0) {
      this.doubleRemaining = Math.max(0, this.doubleRemaining - unscaledDt);
      if (this.doubleRemaining > 0) {
        this.hud.setDoubleTimer(this.doubleRemaining);
      } else {
        this.hud.setDoubleTimer(null);
        this.pushRewardMultiplier();
      }
    }
  }

  // Pulls every uncollected score orb in active segments toward the
  // player. We only target score orbs (gold octahedra) because the
  // power-fantasy of "every coin flies to me" is the mental model;
  // pulling shields/slowmos would surprise the player.
  private applyMagnetField(unscaledDt: number): void {
    this.player.mesh.getWorldPosition(this._world);
    // Use exp-decay for framerate-independent lerp toward target.
    const k = 1 - Math.exp(-MAGNET_LAMBDA * unscaledDt);
    for (const seg of this.streamer.getActive()) {
      if (!seg.active || seg.pickups.length === 0) continue;
      for (const pk of seg.pickups) {
        if (pk.consumed) continue;
        if (pk.type !== "score") continue;
        // Cheap z-distance check using the segment center + zLocal.
        // Segments aren't rotated so worldZ = group.position.z + zLocal.
        const worldZ = seg.group.position.z + pk.mesh.position.z;
        if (Math.abs(worldZ - this._world.z) > MAGNET_RANGE_Z) continue;
        // Translate the player world position into the orb's parent
        // frame and lerp the local position toward it. Body follows
        // via SegmentFactory.syncBodies on the next physics tick.
        seg.group.worldToLocal(this._local.copy(this._world));
        pk.mesh.position.lerp(this._local, k);
      }
    }
  }

  // Hard reset: cleared on startGame / restartFromPause / quitToMenu /
  // onDeath. Restores time-scale and HUD chips to neutral.
  reset(): void {
    if (this.slowmoRemaining > 0) {
      this.app.time.scale = 1.0;
    }
    this.slowmoRemaining = 0;
    this.magnetRemaining = 0;
    this.doubleRemaining = 0;
    this.player.setShield(false);
    this.hud.setShield(false);
    this.hud.setSlowMoTimer(null);
    this.hud.setMagnetTimer(null);
    this.hud.setDoubleTimer(null);
    // Re-assert the difficulty mul (without the double window) on the
    // ScoreSystem so a respawn never inherits a leftover 2x.
    this.pushRewardMultiplier();
  }

  // Whitelist consumable pickup types. Pass null to allow all (the
  // default). MorphHexGame calls this with the daily modifier; we
  // also re-call(null) on every state transition that resets the
  // pickup manager so non-daily runs aren't accidentally restricted.
  setAllowedTypes(types: PickupType[] | null): void {
    if (!types) {
      this.allowedTypes = null;
      return;
    }
    this.allowedTypes = new Set(types);
  }

  // MorphHexGame.startGame pushes the difficulty's scoreMul here so
  // EVERY score gain on the run is scaled. The double-pickup window
  // multiplies on top via pushRewardMultiplier.
  setDifficultyScoreMul(mul: number): void {
    this.difficultyScoreMul = Math.max(0, mul);
    this.pushRewardMultiplier();
  }

  // Recompute and push the final reward multiplier into the
  // ScoreSystem. Called whenever the difficulty changes or the
  // double-pickup window starts/expires.
  private pushRewardMultiplier(): void {
    const doubleMul = this.doubleRemaining > 0 ? DOUBLE_MUL : 1;
    this.score.setRewardMultiplier(this.difficultyScoreMul * doubleMul);
  }

  dispose(): void {
    this.unsub?.();
    this.shieldUnsub?.();
    this.unsub = null;
    this.shieldUnsub = null;
  }

  private handlePickup(ev: PickupHitEvent): void {
    if (!ev.pickup || ev.pickup.consumed) return;
    // Daily "shield_off" filters here (rather than at spawn time) so
    // the level data is unchanged across all daily modifiers; the
    // orb still visually spawns and disappears -- it just has no
    // gameplay effect. Cheaper than re-walking every spec.
    if (this.allowedTypes && !this.allowedTypes.has(ev.type)) {
      this.factory.consumePickup(ev.pickup);
      return;
    }
    const pos = this.copyMeshWorldPos(ev.pickup);

    switch (ev.type) {
      case "score":
        this.applyScore(pos);
        break;
      case "shield":
        this.applyShield(pos);
        break;
      case "slowmo":
        this.applySlowMo(pos);
        break;
      case "magnet":
        this.applyMagnet(pos);
        break;
      case "double":
        this.applyDouble(pos);
        break;
    }

    this.factory.consumePickup(ev.pickup);
    this.onPickupConsumed.emit({ type: ev.type });
  }

  private applyScore(pos: THREE.Vector3): void {
    const gain = Math.round(SCORE_BONUS * this.score.multiplier);
    this.score.addBonus(gain);
    this.hud.spawnPopup(`+${gain}`, "warm");
    this.sparks.burstAt(pos, SCORE_COLOR, 18);
  }

  private applyShield(pos: THREE.Vector3): void {
    this.player.setShield(true);
    this.hud.setShield(true);
    this.hud.spawnPopup("SHIELD", "accent");
    this.sparks.burstAt(pos, SHIELD_COLOR, 22);
  }

  private applySlowMo(pos: THREE.Vector3): void {
    this.slowmoRemaining = SLOWMO_DURATION_S;
    this.app.time.scale = SLOWMO_SCALE;
    this.hud.setSlowMoTimer(this.slowmoRemaining);
    this.hud.spawnPopup("SLOW MO", "hot");
    this.sparks.burstAt(pos, SLOWMO_COLOR, 26);
  }

  // Magnet: arms / refreshes the pull-in window. Stacking with another
  // magnet pickup just resets the timer (we don't accumulate beyond
  // MAGNET_DURATION_S so the player can't stockpile a 30s field).
  private applyMagnet(pos: THREE.Vector3): void {
    this.magnetRemaining = MAGNET_DURATION_S;
    this.hud.setMagnetTimer(this.magnetRemaining);
    this.hud.spawnPopup("MAGNET", "accent");
    this.hud.flash("gate", "#b265ff");
    this.sparks.burstAt(pos, MAGNET_COLOR, 24);
  }

  // Double-score: arms / refreshes the bonus window. pushRewardMultiplier
  // is what actually flips the ScoreSystem; the timer just controls
  // how long the multiplier stays elevated.
  private applyDouble(pos: THREE.Vector3): void {
    const wasInactive = this.doubleRemaining <= 0;
    this.doubleRemaining = DOUBLE_DURATION_S;
    this.hud.setDoubleTimer(this.doubleRemaining);
    this.hud.spawnPopup("2x SCORE", "hot");
    this.hud.flash("gate", "#ffe070");
    this.sparks.burstAt(pos, DOUBLE_COLOR, 28);
    // Only push the multiplier on rising edge so the ScoreSystem
    // doesn't re-emit the same value on every refresh.
    if (wasInactive) this.pushRewardMultiplier();
  }

  private copyMeshWorldPos(pickup: PickupInstance): THREE.Vector3 {
    // The pickup's mesh is parented to the segment.group, which is a
    // child of worldGroup. Walk the matrices to get a world-space pos
    // for the spark burst.
    return pickup.mesh.getWorldPosition(new THREE.Vector3());
  }
}
